import React from 'react';
import { supabase } from '../../lib/supabase';
import {
  sugerirTrechoRegra, contaValidaPara, pendentesQueCasam, aplicarRegrasAosPendentes, agruparAtualizacoes,
} from '../../utils/contabil/classificacao';
import { codigoDaConta } from '../../utils/extrato/montarImportacao';
import {
  TEXTO, SUAVE, BORDA, VERDE, AMBAR, VERMELHO, ROXO, fmtMoeda, fmtData,
  botaoPrimario, botaoSecundario, botaoPequeno, th, td, desabilitado, srOnly, SeletorConta,
} from './comum';

// Etapa 2 — Classificar os lançamentos do banco que estão em "A Classificar".
// Grava só o lado nulo (UPDATE ... WHERE lado IS NULL): se outra pessoa já
// classificou, a linha não é sobrescrita.

const LOTE_IDS = 150;
const ROTULO_SENTIDO = { entrada: 'Entradas', saida: 'Saídas', ambos: 'Entradas e saídas' };

async function gravarGrupos(grupos) {
  let feitos = 0;
  for (const g of grupos) {
    for (let i = 0; i < g.ids.length; i += LOTE_IDS) {
      const { data, error } = await supabase.from('contabil_lancamentos')
        .update({ [g.campo]: g.conta })
        .in('id', g.ids.slice(i, i + LOTE_IDS))
        .is(g.campo, null)
        .select('id');
      if (error) { const e = error; e.feitos = feitos; throw e; }
      feitos += (data || []).length;
    }
  }
  return feitos;
}

function RegrasPainel({ regrasDb, qtdRegrasArquivo, contas, inputStyle, onAlterado, setAviso, somenteLeitura }) {
  const [editando, setEditando] = React.useState(null); // { id, padrao, conta, sentido }
  const [excluindo, setExcluindo] = React.useState(null);
  const [salvando, setSalvando] = React.useState(false);

  const ordenadas = React.useMemo(
    () => [...regrasDb].sort((a, b) => String(a.padrao).localeCompare(String(b.padrao), 'pt-BR')),
    [regrasDb],
  );

  const salvar = async () => {
    if (!editando || !editando.padrao.trim() || !editando.conta) return;
    setSalvando(true);
    const { error } = await supabase.from('contabil_regras')
      .update({ padrao: editando.padrao.trim(), conta_contabil: editando.conta, sentido: editando.sentido })
      .eq('id', editando.id);
    setSalvando(false);
    if (error) {
      console.error('[fechamento-mes] Falha ao alterar a regra.', error);
      setAviso({ tipo: 'erro', texto: 'Não foi possível salvar a regra. Tente novamente.' });
      return;
    }
    setEditando(null);
    setAviso({ tipo: 'sucesso', texto: 'Regra alterada. Ela vale para as próximas importações e para o botão "Aplicar regras".' });
    onAlterado();
  };

  const excluir = async (id) => {
    setSalvando(true);
    const { error } = await supabase.from('contabil_regras').delete().eq('id', id);
    setSalvando(false);
    setExcluindo(null);
    if (error) {
      console.error('[fechamento-mes] Falha ao excluir a regra.', error);
      setAviso({ tipo: 'erro', texto: 'Não foi possível excluir a regra. Tente novamente.' });
      return;
    }
    setAviso({ tipo: 'sucesso', texto: 'Regra excluída.' });
    onAlterado();
  };

  return (
    <div style={{ border: `1px solid ${BORDA}`, borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
      <h5 style={{ color: TEXTO, margin: '0 0 4px 0', fontSize: 14 }}>Regras de classificação da empresa</h5>
      <p style={{ fontSize: 12, color: SUAVE, margin: '0 0 10px 0' }}>
        Aplicadas na importação do extrato e no botão "Aplicar regras". A regra de trecho mais longo tem prioridade.
        {qtdRegrasArquivo > 0 && ` Além destas, ${qtdRegrasArquivo} regra(s) do arquivo da empresa valem como reserva (alteradas em Configurações).`}
      </p>
      {ordenadas.length === 0 ? (
        <div style={{ fontSize: 12, color: SUAVE }}>Nenhuma regra cadastrada. Crie regras ao classificar um lançamento.</div>
      ) : (
        <div style={{ maxHeight: 320, overflow: 'auto', border: `1px solid ${BORDA}`, borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>A descrição contém</th>
                <th style={th}>Conta</th>
                <th style={th}>Vale para</th>
                <th style={th}><span style={srOnly}>Ações</span></th>
              </tr>
            </thead>
            <tbody>
              {ordenadas.map(r => (editando && editando.id === r.id ? (
                <tr key={r.id}>
                  <td style={td}>
                    <input aria-label="Trecho da descrição" value={editando.padrao} onChange={e => setEditando({ ...editando, padrao: e.target.value })} style={inputStyle} />
                  </td>
                  <td style={{ ...td, minWidth: 240 }}>
                    <SeletorConta id={`regra-${r.id}`} contas={contas} valor={editando.conta} onChange={v => setEditando({ ...editando, conta: v })} inputStyle={inputStyle} />
                  </td>
                  <td style={td}>
                    <select aria-label="Vale para" value={editando.sentido} onChange={e => setEditando({ ...editando, sentido: e.target.value })} style={inputStyle}>
                      {Object.entries(ROTULO_SENTIDO).map(([v, rot]) => <option key={v} value={v}>{rot}</option>)}
                    </select>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={salvar} disabled={salvando || !editando.padrao.trim() || !editando.conta} style={desabilitado({ ...botaoPequeno, color: ROXO, border: `1px solid ${ROXO}` }, salvando || !editando.padrao.trim() || !editando.conta)}>Salvar</button>{' '}
                    <button onClick={() => setEditando(null)} style={botaoPequeno}>Cancelar</button>
                  </td>
                </tr>
              ) : (
                <tr key={r.id}>
                  <td style={td}>{r.padrao}</td>
                  <td style={td}>{r.conta_contabil}</td>
                  <td style={td}>{ROTULO_SENTIDO[r.sentido] || r.sentido}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {somenteLeitura ? null : excluindo === r.id ? (
                      <>
                        <span style={{ fontSize: 12, color: VERMELHO, marginRight: 6 }}>Excluir a regra?</span>
                        <button onClick={() => excluir(r.id)} disabled={salvando} style={{ ...botaoPequeno, color: VERMELHO, border: `1px solid ${VERMELHO}` }}>Excluir</button>{' '}
                        <button onClick={() => setExcluindo(null)} style={botaoPequeno}>Cancelar</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { setExcluindo(null); setEditando({ id: r.id, padrao: r.padrao, conta: r.conta_contabil, sentido: r.sentido || 'ambos' }); }} style={botaoPequeno}>Editar</button>{' '}
                        <button onClick={() => { setEditando(null); setExcluindo(r.id); }} style={botaoPequeno}>Excluir</button>
                      </>
                    )}
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function EtapaClassificar({
  empresaSlug, pendentes, aClassificarArquivo, contas, regrasDb, regras, qtdRegrasArquivo,
  mesFechado, nomeMes, onAlterado, onIrEtapa, setAviso, cardStyle, inputStyle, labelStyle,
}) {
  const [editandoId, setEditandoId] = React.useState(null);
  const [contaEdit, setContaEdit] = React.useState('');
  const [criarRegra, setCriarRegra] = React.useState(false);
  const [trecho, setTrecho] = React.useState('');
  const [sentidoRegra, setSentidoRegra] = React.useState('ambos');
  const [selecionados, setSelecionados] = React.useState(() => new Set());
  const [contaLote, setContaLote] = React.useState('');
  const [mostrarRegras, setMostrarRegras] = React.useState(false);
  const [confirmarRegras, setConfirmarRegras] = React.useState(false);
  const [salvando, setSalvando] = React.useState(false);

  // Seleção só de linhas que ainda estão pendentes.
  React.useEffect(() => {
    setSelecionados(prev => {
      const ids = new Set(pendentes.map(p => p.id));
      const novo = new Set([...prev].filter(id => ids.has(id)));
      return novo.size === prev.size ? prev : novo;
    });
    if (editandoId && !pendentes.some(p => p.id === editandoId)) setEditandoId(null);
  }, [pendentes, editandoId]);

  const pendenteEdit = pendentes.find(p => p.id === editandoId) || null;

  const abrir = (p) => {
    setEditandoId(p.id);
    setContaEdit('');
    setCriarRegra(false);
    setTrecho(sugerirTrechoRegra(p.historico));
    setSentidoRegra(p.sentido);
  };

  const extrasRegra = React.useMemo(() => {
    if (!pendenteEdit || !criarRegra || !contaEdit || !trecho.trim()) return [];
    return pendentesQueCasam(pendentes, { padrao: trecho, sentido: sentidoRegra, conta: contaEdit }, pendenteEdit.id);
  }, [pendentes, pendenteEdit, criarRegra, contaEdit, trecho, sentidoRegra]);

  const casamentoRegras = React.useMemo(() => aplicarRegrasAosPendentes(pendentes, regras), [pendentes, regras]);

  const executar = async (itens, { regraNova } = {}) => {
    setSalvando(true);
    setAviso(null);
    try {
      if (regraNova) {
        const { error } = await supabase.from('contabil_regras').insert({ empresa_slug: empresaSlug, ...regraNova });
        if (error) throw Object.assign(error, { etapa: 'regra' });
      }
      const feitos = await gravarGrupos(agruparAtualizacoes(itens));
      const ignorados = itens.length - feitos;
      setAviso({
        tipo: 'sucesso',
        texto: `${feitos} lançamento(s) classificado(s)${regraNova ? ' e regra gravada' : ''}.`
          + (ignorados > 0 ? ` ${ignorados} já tinha(m) sido classificado(s) por outra pessoa e não foi(ram) alterado(s).` : ''),
      });
      setEditandoId(null);
      setSelecionados(new Set());
      setContaLote('');
      setConfirmarRegras(false);
    } catch (err) {
      console.error('[fechamento-mes] Falha ao classificar lançamentos.', err);
      setAviso({
        tipo: 'erro',
        texto: err && err.etapa === 'regra'
          ? 'Não foi possível gravar a regra. Nenhum lançamento foi alterado.'
          : err && err.feitos
            ? `Parte da classificação foi gravada (${err.feitos} de ${itens.length}). Confira a lista e tente novamente.`
            : 'Não foi possível gravar a classificação. Tente novamente; se o problema continuar, acione o suporte.',
      });
    } finally {
      setSalvando(false);
      onAlterado();
    }
  };

  const salvarUm = () => {
    if (!pendenteEdit || !contaValidaPara(pendenteEdit, contaEdit)) return;
    const itens = [pendenteEdit, ...extrasRegra].map(p => ({ pendente: p, conta: contaEdit }));
    const regraNova = criarRegra && trecho.trim()
      ? { padrao: trecho.trim(), conta_contabil: contaEdit, sentido: sentidoRegra }
      : null;
    executar(itens, { regraNova });
  };

  const selecionadosLista = pendentes.filter(p => selecionados.has(p.id));
  const loteValidos = contaLote ? selecionadosLista.filter(p => contaValidaPara(p, contaLote)) : [];
  const loteInvalidos = contaLote ? selecionadosLista.length - loteValidos.length : 0;

  const alternar = (id) => setSelecionados(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const todosMarcados = pendentes.length > 0 && selecionados.size === pendentes.length;

  const contaEditInvalida = pendenteEdit && contaEdit && !contaValidaPara(pendenteEdit, contaEdit);

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        <h4 style={{ color: TEXTO, margin: 0 }}>Lançamentos a classificar</h4>
        <button onClick={() => setMostrarRegras(v => !v)} style={botaoPequeno} aria-expanded={mostrarRegras}>
          {mostrarRegras ? 'Ocultar regras' : `Gerenciar regras (${regrasDb.length})`}
        </button>
      </div>
      <p style={{ fontSize: 12, color: SUAVE, margin: '0 0 12px 0' }}>
        Lançamentos do sistema em {nomeMes} sem conta contábil em um dos lados (A Classificar). Escolha a conta e salve; para vários de uma vez, marque as linhas.
      </p>

      {mostrarRegras && (
        <RegrasPainel
          regrasDb={regrasDb}
          qtdRegrasArquivo={qtdRegrasArquivo}
          contas={contas}
          inputStyle={inputStyle}
          onAlterado={onAlterado}
          setAviso={setAviso}
          somenteLeitura={false}
        />
      )}

      {aClassificarArquivo > 0 && (
        <div style={{ fontSize: 12, color: AMBAR, marginBottom: 12 }}>
          {aClassificarArquivo} lançamento(s) dos arquivos de lançamento estão sem conta (9.9.9). Esses precisam ser corrigidos no arquivo da empresa, não por esta tela.
        </div>
      )}

      {mesFechado ? (
        <div style={{ fontSize: 13, color: SUAVE }}>O mês está fechado. Para classificar, reabra o período na etapa 4.</div>
      ) : pendentes.length === 0 ? (
        <div style={{ fontSize: 13, color: VERDE, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          Nenhum lançamento do sistema em A Classificar neste mês.
          <button onClick={() => onIrEtapa(3)} style={botaoSecundario}>Ir para a conferência das contas</button>
        </div>
      ) : (
        <>
          {casamentoRegras.length > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: '#f7f5fb', border: `1px solid ${BORDA}`, borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: TEXTO }}>
              <span>As regras cadastradas classificam {casamentoRegras.length} destes lançamento(s).</span>
              {confirmarRegras ? (
                <>
                  <button onClick={() => executar(casamentoRegras.map(c => ({ pendente: c.pendente, conta: c.conta })))} disabled={salvando} style={desabilitado({ ...botaoPequeno, color: ROXO, border: `1px solid ${ROXO}` }, salvando)}>
                    Confirmar ({casamentoRegras.length})
                  </button>
                  <button onClick={() => setConfirmarRegras(false)} style={botaoPequeno}>Cancelar</button>
                </>
              ) : (
                <button onClick={() => setConfirmarRegras(true)} style={botaoPequeno}>Aplicar regras</button>
              )}
            </div>
          )}

          {selecionados.size > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: 'rgba(124,58,237,0.06)', border: `1px solid ${BORDA}`, borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: TEXTO, fontWeight: 600 }}>{selecionados.size} selecionado(s)</span>
              <div style={{ flex: '1 1 260px', minWidth: 220 }}>
                <SeletorConta id="fm-conta-lote" contas={contas} valor={contaLote} onChange={setContaLote} inputStyle={inputStyle} />
              </div>
              <button
                onClick={() => executar(loteValidos.map(p => ({ pendente: p, conta: contaLote })))}
                disabled={salvando || loteValidos.length === 0}
                style={desabilitado(botaoPrimario, salvando || loteValidos.length === 0)}
              >
                {salvando ? 'Gravando…' : `Aplicar a ${loteValidos.length || selecionados.size}`}
              </button>
              <button onClick={() => setSelecionados(new Set())} style={botaoPequeno}>Limpar seleção</button>
              {loteInvalidos > 0 && (
                <span style={{ fontSize: 12, color: AMBAR, width: '100%' }}>
                  {loteInvalidos} lançamento(s) selecionado(s) já usam essa conta do outro lado e ficarão de fora.
                </span>
              )}
            </div>
          )}

          <div style={{ maxHeight: 520, overflow: 'auto', border: `1px solid ${BORDA}`, borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 28 }}>
                    <input type="checkbox" aria-label="Selecionar todos" checked={todosMarcados} onChange={() => setSelecionados(todosMarcados ? new Set() : new Set(pendentes.map(p => p.id)))} />
                  </th>
                  <th style={th}>Data</th>
                  <th style={th}>Histórico</th>
                  <th style={{ ...th, textAlign: 'right' }}>Valor</th>
                  <th style={th}>Outro lado</th>
                  <th style={th}><span style={srOnly}>Ações</span></th>
                </tr>
              </thead>
              <tbody>
                {pendentes.map(p => {
                  const aberto = p.id === editandoId;
                  return (
                    <React.Fragment key={p.id}>
                      <tr style={{ background: aberto ? 'rgba(124,58,237,0.06)' : 'transparent' }}>
                        <td style={td}>
                          <input type="checkbox" aria-label={`Selecionar ${p.historico}`} checked={selecionados.has(p.id)} onChange={() => alternar(p.id)} />
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtData(p.data)}</td>
                        <td style={td}>{p.historico}</td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', color: p.sentido === 'entrada' ? VERDE : VERMELHO }}>
                          {p.sentido === 'entrada' ? '' : '−'}{fmtMoeda(p.valor)}
                        </td>
                        <td style={td}>{p.contaOutroLado || '—'}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                          {aberto
                            ? <button onClick={() => setEditandoId(null)} style={botaoPequeno}>Fechar</button>
                            : <button onClick={() => abrir(p)} style={{ ...botaoPequeno, color: ROXO, border: `1px solid ${ROXO}` }}>Classificar</button>}
                        </td>
                      </tr>
                      {aberto && (
                        <tr>
                          <td colSpan={6} style={{ ...td, background: '#fbfaff', padding: '12px 14px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 640 }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <label htmlFor={`fm-conta-${p.id}`} style={labelStyle}>
                                  Conta contábil ({p.campo === 'conta_debito' ? 'lado débito' : 'lado crédito'})
                                </label>
                                <SeletorConta id={`fm-conta-${p.id}`} contas={contas} valor={contaEdit} onChange={setContaEdit} inputStyle={inputStyle} />
                                {contaEditInvalida && (
                                  <span style={{ fontSize: 12, color: AMBAR }}>
                                    Essa conta já está no outro lado do lançamento ({codigoDaConta(p.contaOutroLado)}). Escolha a contrapartida.
                                  </span>
                                )}
                              </div>

                              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: TEXTO }}>
                                <input type="checkbox" checked={criarRegra} onChange={e => setCriarRegra(e.target.checked)} />
                                Criar regra
                              </label>
                              {criarRegra && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 24 }}>
                                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, color: TEXTO }}>
                                    <span>Sempre que a descrição contiver</span>
                                    <input aria-label="Trecho da descrição" value={trecho} onChange={e => setTrecho(e.target.value)} style={{ ...inputStyle, width: 220 }} placeholder="trecho da descrição" />
                                    <span>usar esta conta, em</span>
                                    <select aria-label="Vale para" value={sentidoRegra} onChange={e => setSentidoRegra(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
                                      {Object.entries(ROTULO_SENTIDO).map(([v, rot]) => <option key={v} value={v}>{rot.toLowerCase()}</option>)}
                                    </select>
                                  </div>
                                  {!trecho.trim() && <span style={{ fontSize: 12, color: AMBAR }}>Informe o trecho da descrição.</span>}
                                  {trecho.trim() && contaEdit && (
                                    <span style={{ fontSize: 12, color: extrasRegra.length ? ROXO : SUAVE }}>
                                      {extrasRegra.length
                                        ? `Outros ${extrasRegra.length} lançamento(s) pendente(s) do mês contêm este trecho e também serão classificados.`
                                        : 'Nenhum outro lançamento pendente do mês contém este trecho.'}
                                    </span>
                                  )}
                                </div>
                              )}

                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {(() => {
                                  const off = salvando || !contaValidaPara(p, contaEdit) || (criarRegra && !trecho.trim());
                                  return (
                                    <button onClick={salvarUm} disabled={off} style={desabilitado(botaoPrimario, off)}>
                                      {salvando ? 'Gravando…' : extrasRegra.length ? `Salvar e classificar ${extrasRegra.length + 1}` : 'Salvar'}
                                    </button>
                                  );
                                })()}
                                <button onClick={() => setEditandoId(null)} disabled={salvando} style={botaoSecundario}>Cancelar</button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
